import { X, Folder as FolderIcon, Home } from 'lucide-react';
import { useAllDossierFolders, useMoveFile } from '../../hooks/queries/useDossierFiles';
import { Button } from '../ui/Button';
import type { DossierFile } from '../../services/files/filesService';

interface Props {
  dossierId: string;
  file: DossierFile;
  /** Autres fichiers déplacés avec `file` (sélection multiple). */
  others?: DossierFile[];
  onClose: () => void;
}

export function MoveFileModal({ dossierId, file, others = [], onClose }: Props) {
  const batch = others.length > 0;
  const { data: folders = [] } = useAllDossierFolders(dossierId);
  const move = useMoveFile(dossierId);

  async function handleMove(toFolderId: string | null) {
    if (!batch && toFolderId === file.folder_id) {
      onClose();
      return;
    }
    try {
      for (const item of [file, ...others]) {
        if (item.folder_id !== toFolderId) await move.mutateAsync({ fileId: item.id, toFolderId });
      }
      onClose();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur déplacement');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-sm max-h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <h2 className="font-heading text-lg font-semibold">
            {batch ? `Déplacer ${others.length + 1} documents` : `Déplacer « ${file.filename} »`}
          </h2>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="flex-1 overflow-auto py-2">
          <button
            type="button"
            disabled={(!batch && file.folder_id === null) || move.isPending}
            onClick={() => handleMove(null)}
            className="flex items-center gap-2 w-full px-5 py-2 text-sm hover:bg-neutral-50 text-left disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Home className="w-4 h-4 text-neutral-500" /> Racine du dossier
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              type="button"
              disabled={(!batch && f.id === file.folder_id) || move.isPending}
              onClick={() => handleMove(f.id)}
              className="flex items-center gap-2 w-full px-5 py-2 text-sm hover:bg-neutral-50 text-left disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ paddingLeft: `${20 + f.depth * 16}px` }}
            >
              <FolderIcon className="w-4 h-4 text-cime-primary" /> {f.name}
            </button>
          ))}
        </div>
        <footer className="flex items-center justify-end px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button variant="ghost" type="button" onClick={onClose} disabled={move.isPending}>
            Annuler
          </Button>
        </footer>
      </div>
    </div>
  );
}
